import type { DropIndicatorTarget, SortableItemRect } from './types';
import { isFiniteNumber } from './untils';

function normalizeItemRects({
  itemRects,
}: {
  itemRects: readonly SortableItemRect[];
}): SortableItemRect[] {
  return [...itemRects].sort(
    (leftRect, rightRect) => leftRect.top - rightRect.top
  );
}

/**
 * Resolves insertion line target from the current pointer Y position.
 */
export function getDropIndicatorTarget({
  pointerY,
  itemRects,
}: {
  pointerY: number;
  itemRects: readonly SortableItemRect[];
}): DropIndicatorTarget | null {
  if (!isFiniteNumber(pointerY)) return null;
  if (!itemRects.length) return null;

  const orderedItemRects = normalizeItemRects({ itemRects });

  for (let itemIndex = 0; itemIndex < orderedItemRects.length; itemIndex += 1) {
    const itemRect = orderedItemRects[itemIndex];
    const itemMidpoint = itemRect.top + itemRect.height / 2;

    if (pointerY < itemMidpoint) {
      return {
        index: itemRect.index,
        side: 'before',
      };
    }
  }

  const lastItemRect = orderedItemRects[orderedItemRects.length - 1];

  return {
    index: lastItemRect.index,
    side: 'after',
  };
}

/**
 * Converts line target (before/after item index) to insertion index.
 */
export function getInsertionIndexFromTarget({
  target,
  listLength,
}: {
  target: DropIndicatorTarget | null;
  listLength: number;
}): number | null {
  if (!target) return null;
  if (!isFiniteNumber(listLength)) return null;
  if (listLength <= 0) return null;

  if (target.side === 'before') {
    return Math.max(0, Math.min(target.index, listLength));
  }

  return Math.max(0, Math.min(target.index + 1, listLength));
}
